import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Menu, X, Home, Building2, UserCircle, Users, Shield,
  Warehouse, Truck, Package, Clock, Search, ChevronDown, ChevronRight, Megaphone
} from 'lucide-react';
import { usePermission } from '../context/PermissionContext';
import { useAnnouncements } from '../context/AnnouncementContext';

const SideNav = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const { hasPermission } = usePermission();
  const { isAdmin: isAnnouncementAdmin } = useAnnouncements();

  const navigationItems = [
    {
      category: 'Home',
      icon: <Home className="w-5 h-5" />,
      path: '/', // Add direct path for Home
      isDirectLink: true, // Add this flag to identify direct links
      permission: 'home_section',
      items: [] // Empty array since we don't need dropdown items
    },
    {
      category: 'Company',
      icon: <Building2 className="w-5 h-5" />,
      permission: 'company_section',
      items: [
        { name: 'Company Profile', path: '/company/profile', permission: 'company_settings' },
        { name: 'Locations', path: '/company/locations', permission: 'company_settings' },
        { name: 'Departments', path: '/company/departments', permission: 'company_settings' },
        { name: 'Billing', path: '/company/billing', permission: 'billing_access' },
        { name: 'Integration Settings', path: '/company/integrations', permission: 'integrations' }
      ]
    },
    {
      category: 'Warehouse Operations',
      icon: <Warehouse className="w-5 h-5" />,
      permission: 'warehouse_operations',
      items: [
        { name: 'Truck Scheduler', path: '/tiles/truck_scheduler/inbound/calendar', permission: 'truck_scheduler' }
      ]
    },
    {
      category: 'Profile',
      icon: <UserCircle className="w-5 h-5" />,
      permission: 'profile_section',
      items: [
        { name: 'My Profile', path: '/profile', permission: 'profile_section' },
        { name: 'Preferences', path: '/profile/preferences', permission: 'profile_section' },
        { name: 'Security', path: '/profile/security', permission: 'profile_section' },
        { name: 'Notifications', path: '/profile/notifications', permission: 'profile_section' }
      ]
    },
    {
      category: 'Users',
      icon: <Users className="w-5 h-5" />,
      permission: 'users_section',
      items: [
        { name: 'All Users', path: '/users/all', permission: 'view_users' },
        { name: 'User Groups', path: '/users/groups', permission: 'view_users' },
        { name: 'Access Logs', path: '/users/logs', permission: 'view_access_logs' }
      ]
    },
    {
      category: 'Roles',
      icon: <Shield className="w-5 h-5" />,
      permission: 'roles_section',
      items: [
        { name: 'Role Management', path: '/roles/manage', permission: 'manage_roles' },
        { name: 'Create Role', path: '/roles/create', permission: 'manage_roles' },
        { name: 'Permissions', path: '/roles/permissions', permission: 'manage_roles' },
        { name: 'Role Assignment', path: '/roles/assign', permission: 'manage_roles' }
      ]
    },
    {
      // Gated by announcement_admins membership rather than a permission code
      category: 'Announcements',
      icon: <Megaphone className="w-5 h-5" />,
      announcementAdminOnly: true,
      items: [
        { name: 'Manage Announcements', path: '/announcements/manage' }
      ]
    }
  ];

  // First, filter by permissions
  const permissionFilteredNavigation = navigationItems.filter(section => {
    if (section.announcementAdminOnly) {
      return isAnnouncementAdmin;
    }

    // Check if user has permission to see this section
    if (!hasPermission(section.permission)) {
      return false;
    }
    
    // For sections with sub-items, filter those items by permission as well
    if (!section.isDirectLink) {
      section.items = section.items.filter(item => hasPermission(item.permission));
      
      // If all sub-items are filtered out, don't show the section
      if (section.items.length === 0) {
        return false;
      }
    }
    
    return true;
  });
  
  // Then filter by search term
  const filteredNavigation = permissionFilteredNavigation.filter(section => {
    if (!searchTerm) return true;
    
    // Handle direct links (like Home)
    if (section.isDirectLink) {
      return section.category.toLowerCase().includes(searchTerm.toLowerCase());
    }
    
    // Handle expandable sections
    return section.items.some(item => 
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  return (
    <>
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md bg-slate-800 text-slate-200"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      >
        {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div
        className={`fixed lg:static inset-y-0 left-0 z-40 transition-transform duration-300 transform 
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          h-screen bg-slate-800 border-r border-slate-700 w-64 overflow-y-auto`}
      >
        <div className="p-4 border-b border-slate-700/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="w-full pl-10 pr-4 py-2 text-sm bg-slate-700/30 border border-slate-600/50 rounded-md 
                     focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50 
                     text-slate-200 placeholder-slate-400"
            />
          </div>
        </div>

        <nav className="mt-4">
          {filteredNavigation.map((section) => (
            <div key={section.category} className="mb-2">
              {section.isDirectLink ? (
                // Direct link button for Home
                <Link
                  to={section.path}
                  className="w-full flex items-center px-4 py-2 text-slate-200 hover:bg-slate-700/50 transition-colors"
                >
                  <span className="mr-3 text-slate-400">{section.icon}</span>
                  <span className="flex-1 text-left">{section.category}</span>
                </Link>
              ) : (
                // Expandable category button (unchanged)
                <>
                  <button
                    onClick={() => toggleCategory(section.category)}
                    className="w-full flex items-center px-4 py-2 text-slate-200 hover:bg-slate-700/50 transition-colors"
                  >
                    <span className="mr-3 text-slate-400">{section.icon}</span>
                    <span className="flex-1 text-left">{section.category}</span>
                    {expandedCategories[section.category] ? 
                      <ChevronDown className="w-4 h-4 text-slate-400" /> : 
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    }
                  </button>
                  
                  {expandedCategories[section.category] && (
                    <div className="ml-4 pl-4 border-l border-slate-700/50">
                      {section.items.map(item => (
                        <Link 
                          key={item.path} 
                          to={item.path}
                          className="block py-2 px-4 text-sm text-slate-300 hover:bg-slate-700/30 
                                   hover:text-slate-100 rounded-md transition-colors"
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </nav>
      </div>
    </>
  );
};

export default SideNav;
